import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Select } from '@/components/common';
import { plansSelector } from '@/modules/AdminTools/redux/planBuilder/plans';
import { CustomErrorMessage } from '@/components/common/Form';
import { defaultSelectOption } from '@/modules/AdminTools/lib/constants';

const PlanSelect = ({ planId, label, other }) => {
  const { setValue } = useFormContext();
  const plans = useSelector(plansSelector);
  const otherValue = useWatch({ name: other });
  const currentValue = useWatch({ name: planId });

  const errors = useSelector(
    (state) => state?.errors?.errors?.planUpgradePathsUpdate?.[planId],
  );

  const otherValueErrors = useSelector(
    (state) => state?.errors?.errors?.planUpgradePathsUpdate?.[other],
  );

  const filteredPlans = useMemo(() => {
    return plans.filter(({ id }) => id != otherValue);
  }, [otherValue, plans]);

  const options = useMemo(() => {
    if (!filteredPlans) {
      return [];
    }

    return [
      ...(errors?.length || otherValueErrors?.length ? [] : [defaultSelectOption]),
      ...filteredPlans.map(({ id, name }) => ({
        label: `${id} - ${name}`,
        value: id,
      })),
    ];
  }, [filteredPlans, errors?.length, otherValueErrors?.length]);

  useEffect(() => {
    if (!options.length || currentValue) {
      return;
    }

    const timeout = setTimeout(() => {
      setValue(planId, '');
    }, 150);

    return () => clearTimeout(timeout);
  }, [options, setValue, planId, currentValue]);

  return (
    <div className="flex flex-col w-64">
      <div className="flex items-center">
        <div className="flex text-sm font-medium text-gray-700">
          <label>
            {label}
            <span className="text-red-500">{' *'}</span>
          </label>
        </div>
      </div>
      <div className="mt-0.5 block rounded-md shadow-sm">
        <Controller
          name={planId}
          render={({ field: { onChange, value, name } }) => (
            <Select
              {...{
                name,
                onChange,
                value,
                options,
              }}
              hasError={Boolean(errors?.length)}
              required
            />
          )}
        />
      </div>
      {errors?.length && <CustomErrorMessage text={errors[0]} />}
    </div>
  );
};

PlanSelect.propTypes = {
  label: PropTypes.string,
  other: PropTypes.string,
  planId: PropTypes.string,
};

export default PlanSelect;
